import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import './AlertStatusSetup.scss';
import { SalvButton, useProgressStepper, useStep } from '@salvapp/ui-components';
import AlertStatusSetupStepper from './AlertStatusSetupStepper';
import AlertStatusSetupContext from './AlertStatusSetupContext';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import { useDispatch } from 'react-redux';
import GeneralSetup from './GeneralSetup';
import { FormikProps } from 'formik';
import { getInitialSetupData, setupDataToRequest } from './mapper';
import { createAlertStatus, deleteAlertStatus, fetchAlertStatuses, updateAlertStatus } from '../alertStatusesActions';
import ScreeningSetup from './ScreeningSetup';
import useCustomLists from '../../../custom-lists/useCustomLists';
import SetupSummary from './SetupSummary';
import MonitoringSetup from './MonitoringSetup';
import { FormikErrors } from 'formik/dist/types';
import useAlertStatus from './useAlertStatus';
import useGeneralSetupData from './useGeneralSetupData';
import useScreeningSetupData from './useScreeningSetupData';
import useMonitoringSetupData from './useMonitoringSetupData';
import SalvPage from '../../../common/SalvPage';

export enum AlertStatusSetupStep {
  GENERAL = 1,
  SCREENING = 2,
  MONITORING = 3,
  SUMMARY = 4,
}

const AlertStatusSetup = () => {
  const navigate = useNavigate();
  const id = Number(useParams<{ id: string }>().id);
  const isEdit = !!id;
  const currentAlertStatus = useAlertStatus(id);

  const customLists = useCustomLists();
  const { initialGeneralSetupData, initialScreeningSetupData, initialMonitoringSetupData } = useMemo(
    () => getInitialSetupData(currentAlertStatus, customLists),
    [currentAlertStatus, customLists]
  );

  const dispatch = useDispatch();

  const { steps, setSteps, goToNextStep, goToPrevStep, goToStep, activeStep } = useProgressStepper({
    steps: 4,
  });

  const { setStep: setGeneralSetupStep } = useStep({
    ordinal: AlertStatusSetupStep.GENERAL,
    setSteps,
  });
  const { setStep: setScreeningSetupStep } = useStep({
    ordinal: AlertStatusSetupStep.SCREENING,
    setSteps,
  });
  const { setStep: setMonitoringSetupStep } = useStep({
    ordinal: AlertStatusSetupStep.MONITORING,
    setSteps,
  });
  const { setStep: setSetupSummaryStep } = useStep({
    ordinal: AlertStatusSetupStep.SUMMARY,
    setSteps,
  });

  const {
    generalSetupData,
    setGeneralSetupData,
    isGeneralSetupValid,
    setGeneralSetupValid,
    isGeneralSetupSubmitted,
    setGeneralSetupSubmitted,
    isGeneralSetupSubmissionTouched,
    setGeneralSetupSubmissionTouched,
  } = useGeneralSetupData(initialGeneralSetupData);

  const {
    screeningSetupData,
    setScreeningSetupData,
    isScreeningSetupValid,
    setScreeningSetupValid,
    isScreeningSetupSubmitted,
    setScreeningSetupSubmitted,
    isScreeningSetupSubmissionTouched,
    setScreeningSetupSubmissionTouched,
  } = useScreeningSetupData(initialScreeningSetupData);

  const {
    monitoringSetupData,
    setMonitoringSetupData,
    isMonitoringSetupValid,
    setMonitoringSetupValid,
    isMonitoringSetupSubmitted,
    setMonitoringSetupSubmitted,
    isMonitoringSetupSubmissionTouched,
    setMonitoringSetupSubmissionTouched,
  } = useMonitoringSetupData(initialMonitoringSetupData);

  // in edit mode the progress stepper must show the data of all enabled alert types
  useEffect(() => {
    if (isEdit) {
      setGeneralSetupSubmitted(true);
      const { isScreeningEnabled, isMonitoringEnabled } = initialGeneralSetupData.alertType;
      if (isScreeningEnabled) {
        setScreeningSetupSubmitted(true);
        setScreeningSetupStep({
          type: 'filled',
        });
      }
      if (isMonitoringEnabled) {
        setMonitoringSetupSubmitted(true);
        setMonitoringSetupStep({
          type: 'filled',
        });
      }
    }
  }, [initialGeneralSetupData]);

  const [alertType, setAlertType] = useState(generalSetupData.alertType);

  useEffect(() => {
    if (alertType.isScreeningEnabled) {
      setScreeningSetupStep({
        isDisabled: false,
      });
    } else {
      setScreeningSetupSubmitted(false);
      setScreeningSetupStep({
        isDisabled: true,
        type: 'pending',
      });
    }
  }, [alertType.isScreeningEnabled]);

  useEffect(() => {
    if (alertType.isMonitoringEnabled) {
      setMonitoringSetupStep({
        isDisabled: false,
      });
    } else {
      setMonitoringSetupSubmitted(false);
      setMonitoringSetupStep({
        isDisabled: true,
        type: 'pending',
      });
    }
  }, [alertType.isMonitoringEnabled]);

  useEffect(() => {
    const isAnyAlertTypeEnabled =
      alertType.isBridgeEnabled ||
      alertType.isScreeningEnabled ||
      alertType.isMonitoringEnabled ||
      alertType.isManualEnabled;
    setSetupSummaryStep({
      isDisabled: !isAnyAlertTypeEnabled,
    });
  }, [alertType]);

  const isLastStep = activeStep === AlertStatusSetupStep.SUMMARY;
  const isNextButtonActive = () => {
    switch (activeStep) {
      case AlertStatusSetupStep.GENERAL:
        return !isGeneralSetupSubmissionTouched || (isGeneralSetupSubmissionTouched && isGeneralSetupValid);
      case AlertStatusSetupStep.SCREENING:
        return !isScreeningSetupSubmissionTouched || (isScreeningSetupSubmissionTouched && isScreeningSetupValid);
      case AlertStatusSetupStep.MONITORING:
        return !isMonitoringSetupSubmissionTouched || (isMonitoringSetupSubmissionTouched && isMonitoringSetupValid);
      case AlertStatusSetupStep.SUMMARY:
        return true;
    }
  };

  const SetupPage = useCallback(() => {
    switch (activeStep) {
      case AlertStatusSetupStep.GENERAL:
        return <GeneralSetup />;
      case AlertStatusSetupStep.SCREENING:
        return <ScreeningSetup />;
      case AlertStatusSetupStep.MONITORING:
        return <MonitoringSetup />;
      case AlertStatusSetupStep.SUMMARY:
        return <SetupSummary />;
    }
  }, [activeStep]);
  const displayDiscardModal = () => {
    dispatch(
      showConfirmationModal({
        title: isEdit
          ? 'Do you want to discard Alert status editing?'
          : 'Do you want to discard Alert status creation?',
        description: 'Changes made will not be saved.',
        onConfirm() {
          navigate('/screening/statuses');
        },
        confirmationText: 'Yes, discard',
        confirmButtonVariant: 'danger',
        cancelText: 'Back',
      })
    );
  };
  const displayDeleteModal = () => {
    dispatch(
      showConfirmationModal({
        title: 'Do you want to delete this Alert status?',
        description: (
          <>
            <div className={'m-b-16'}>
              {`The existing Alert status will be moved to the "Historic Statuses" section. 
              Please note that you won't have the option to restore the historic status once this change is made.`}
            </div>
            <div>
              {`Alerts currently holding this status will retain that status, 
            and you'll still find this status option in the filter dropdowns within the Alerts list view.`}
            </div>
          </>
        ),
        onConfirm: async () => {
          await deleteAlertStatus(id);
          await dispatch(fetchAlertStatuses());
          navigate('/screening/statuses', {
            state: {
              isDirty: true,
            },
          });
        },
        confirmationText: 'Yes, delete',
        confirmButtonVariant: 'danger',
        cancelText: 'Back',
      })
    );
  };

  const setupPageFormRef = useRef<FormikProps<any>>(null);

  const submitAlertStatus = async () => {
    const setupData = {
      generalSetupData,
      screeningSetupData,
      monitoringSetupData,
    };

    try {
      if (isEdit) {
        await updateAlertStatus(id, setupDataToRequest(setupData));
      } else {
        await createAlertStatus(setupDataToRequest(setupData));
      }
      await dispatch(fetchAlertStatuses());
      navigate('/screening/statuses', {
        state: {
          isDirty: true,
        },
      });
    } catch (e) {
      const res = e as Response;
      if (res.status === 400) {
        const { message } = await res.json();
        highlightApiErrors(message);
      }
    }
  };

  const setSetupPageSubmissionTouched = () => {
    switch (activeStep) {
      case AlertStatusSetupStep.GENERAL:
        setGeneralSetupSubmissionTouched(true);
        break;
      case AlertStatusSetupStep.SCREENING:
        setScreeningSetupSubmissionTouched(true);
        break;
      case AlertStatusSetupStep.MONITORING:
        setMonitoringSetupSubmissionTouched(true);
        break;
    }
  };

  const [apiErrors, setApiErrors] = useState<FormikErrors<any>>({});
  const resolveApiErrors = () => setApiErrors({});
  const highlightApiErrors = (message: string) => {
    setApiErrors({ code: message });
    goToStep(AlertStatusSetupStep.GENERAL);
  };

  return (
    <AlertStatusSetupContext.Provider
      value={{
        progressStepper: {
          steps,
          goToNextStep,
          goToPrevStep,
          setGeneralSetupStep,
          setScreeningSetupStep,
          setMonitoringSetupStep,
          activeStep,
        },
        formRef: setupPageFormRef,
        setAlertType,
        generalSetup: {
          data: generalSetupData,
          setData: setGeneralSetupData,
          isValid: isGeneralSetupValid,
          setValid: setGeneralSetupValid,
          isSubmissionTouched: isGeneralSetupSubmissionTouched,
          isSubmitted: isGeneralSetupSubmitted,
          setSubmitted: setGeneralSetupSubmitted,
        },
        screeningSetup: {
          data: screeningSetupData,
          setData: setScreeningSetupData,
          isValid: isScreeningSetupValid,
          setValid: setScreeningSetupValid,
          isSubmissionTouched: isScreeningSetupSubmissionTouched,
          isSubmitted: isScreeningSetupSubmitted,
          setSubmitted: setScreeningSetupSubmitted,
        },
        monitoringSetup: {
          data: monitoringSetupData,
          setData: setMonitoringSetupData,
          isValid: isMonitoringSetupValid,
          setValid: setMonitoringSetupValid,
          isSubmissionTouched: isMonitoringSetupSubmissionTouched,
          isSubmitted: isMonitoringSetupSubmitted,
          setSubmitted: setMonitoringSetupSubmitted,
        },
        apiErrors,
      }}
    >
      <SalvPage title={isEdit ? 'Edit alert status' : 'Create alert status'}>
        <div className='alert-status-setup'>
          <div className='setup'>
            <SetupPage />
            <div className={'d-flex justify-content-between button-container'}>
              <div className='button-container-group'>
                <SalvButton
                  type='button'
                  variant='muted'
                  label={isEdit ? 'Discard editing' : 'Discard setup'}
                  onClick={() => displayDiscardModal()}
                />
                {isEdit && (
                  <SalvButton
                    type='button'
                    variant='danger'
                    label={'Delete status'}
                    onClick={() => displayDeleteModal()}
                  />
                )}
              </div>
              <div className='button-container-group'>
                {activeStep !== AlertStatusSetupStep.GENERAL && (
                  <SalvButton type='button' variant='muted' label='Back' onClick={() => goToPrevStep()} />
                )}
                <SalvButton
                  type='button'
                  variant='primary'
                  label={isLastStep ? 'Save status' : 'Next'}
                  disabled={!isNextButtonActive()}
                  onClick={async () => {
                    if (isLastStep) {
                      await submitAlertStatus();
                    } else {
                      setSetupPageSubmissionTouched();
                      if (!setupPageFormRef.current?.isValid) {
                        return;
                      }
                      resolveApiErrors();
                      setupPageFormRef.current?.submitForm();
                      goToNextStep();
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <div className='m-t-8'>
            <AlertStatusSetupStepper />
          </div>
        </div>
      </SalvPage>
    </AlertStatusSetupContext.Provider>
  );
};

export default AlertStatusSetup;
